import React from "react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter } from "@/components/site/FinalCTA";
import { PAGES } from "@/constants/testIds";
import { Link } from "react-router-dom";

const chapters = [
  {
    n: "01",
    t: "A living archive, not a database",
    b: "We treat family history like an heirloom, not a spreadsheet. Every entry is designed to feel like a page in a well-kept book.",
  },
  {
    n: "02",
    t: "India-first, diaspora-wide",
    b: "From a Kalpathy tharavad to a Toronto suburb, we hold space for every branch — including the ones that migrated, married in, or were adopted.",
  },
  {
    n: "03",
    t: "Places carry memory",
    b: "Every ancestor is anchored to a village, a district, a temple, a road — because a family without a place is a story without a setting.",
  },
  {
    n: "04",
    t: "Powered by SocialPulse",
    b: "IndianFamilyTree is a child site of the SocialPulse network — connecting genealogy to places, events, culture and community across India and the world.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid={PAGES.about} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="About"
        title="A digital heritage platform for the"
        italic="Indian diaspora."
        subtitle="IndianFamilyTree.com is a modern genealogy and heritage platform designed to help families across India and the global Indian diaspora preserve, explore and celebrate their ancestry. As part of the SocialPulse Network, it combines family history, geography, historical records and storytelling into a living digital archive that can be shared across generations."
      />

      <section className="relative py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4">
            <div className="sticky top-32">
              <span className="text-xs uppercase tracking-[0.3em] text-saffron">Our principles</span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[0.95]">
                Slow craft
                <span className="italic text-tulsi"> over </span>
                quick features.
              </h2>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-8 lg:pl-16">
            <ol className="space-y-12">
              {chapters.map((c, i) => (
                <motion.li
                  key={c.n}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.05 }}
                  className="grid grid-cols-12 gap-4 border-b border-ivory-300 pb-10 last:border-none"
                >
                  <div className="col-span-2 md:col-span-1 font-serif text-2xl text-saffron">{c.n}</div>
                  <div className="col-span-10 md:col-span-11">
                    <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-ink leading-tight">{c.t}</h3>
                    <p className="mt-3 text-ink-50 text-base md:text-lg font-light leading-relaxed max-w-2xl">{c.b}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-tulsi text-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8">
            <span className="text-xs uppercase tracking-[0.3em] text-marigold">SocialPulse Network</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-[0.95]">
              The network of memory.
            </h2>
            <p className="mt-6 text-ivory/70 text-lg font-light max-w-2xl leading-relaxed">
              SocialPulse connects places, events, culture and community across India and the world.
              IndianFamilyTree brings genealogy into that fabric — so your family's story can sit
              alongside the village it comes from, the temple it worships in, and the trade it kept.
            </p>
            <Link
              to="/explore-india"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-medium hover:bg-saffron-light transition"
            >
              Explore India on the network →
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
